import { useEffect, useRef } from "react";
import { useAudioEffects } from "../../../hooks/use-audio-effects";
import { GAME_CONFIG } from "../config/gameConfig";
import { EGG_FIGHT_SOUNDS } from "../config/soundKeys";
import type { GameState } from "../types";

/**
 * Fires sound effects at every meaningful game moment.
 *
 * Uses useAudioEffects() directly so playEffect is always the live instance.
 *
 * @param state     - Current game state (read-only)
 * @param player1Id - The local player's ID; used to determine win vs. lose outcomes
 */
export function useEggFightAudio(state: GameState, player1Id: string): void {
  const { playEffect } = useAudioEffects();

  const { phase, countdownValue, roundWinnerId, gameWinnerId } = state;

  // ── Countdown: tick on each number, fight sound on 0 ──────────────────────
  useEffect(() => {
    if (phase !== "countdown") return;

    playEffect(EGG_FIGHT_SOUNDS.COUNTDOWN_PLUS_FIGHT);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // ── Fight phase: schedule one sound per clash ──────────────────────────────
  const clashTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    clashTimersRef.current.forEach(clearTimeout);
    clashTimersRef.current = [];

    if (phase === "fighting") {
      const { clash1Ms, clash2Ms, clash3Ms } =
        GAME_CONFIG.animation.fightClashes;

      const add = (sfx: string, delay: number): void => {
        const t = setTimeout(() => playEffect(sfx), delay);
        clashTimersRef.current.push(t);
      };

      add(EGG_FIGHT_SOUNDS.CLASH_3, clash1Ms);
      add(EGG_FIGHT_SOUNDS.CLASH_3, clash2Ms);
      add(EGG_FIGHT_SOUNDS.CLASH_3, clash3Ms);
    }

    if (phase === "impact") {
      playEffect(EGG_FIGHT_SOUNDS.FINAL_HIT);
    }

    if (phase === "game-over") {
      if (gameWinnerId === player1Id) {
        playEffect(EGG_FIGHT_SOUNDS.MATCH_WIN);
      } else if (gameWinnerId !== null) {
        playEffect(EGG_FIGHT_SOUNDS.MATCH_LOSE);
      }
    }

    return () => {
      clashTimersRef.current.forEach(clearTimeout);
      clashTimersRef.current = [];
    };
    // playEffect is stable from context; phase is the only re-scheduling trigger
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // ── Round result: win or lose sound when the round resolves ───────────────
  useEffect(() => {
    if (phase !== "round-result" || !roundWinnerId) return;
    if (roundWinnerId === player1Id) {
      playEffect(EGG_FIGHT_SOUNDS.ROUND_WIN);
    } else {
      playEffect(EGG_FIGHT_SOUNDS.ROUND_LOSE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, roundWinnerId]);
}
